/**
 * StreamPreviewPage — Standalone page for shared livestream links.
 *
 * Routing: /watch/:streamId  (lives in AppRoot, OUTSIDE /app/* and beta gate)
 *
 * Logged-in user  → immediate redirect to /app/watch/:streamId
 * Logged-out user → 15-second live preview → redirect to landing page
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import LiveKitStreamViewer from '../LiveKitStreamViewer';
import LiveKitMultiViewer from '../LiveKitMultiViewer';
import AgoraMultiplayerViewer from '../AgoraMultiplayerViewer';
import { Toaster } from '../components/ui/sonner';

const PREVIEW_SECONDS = 15;
const PREVIEW_STORAGE_KEY = 'gems_preview_used';
const VISITOR_ID_KEY = 'gems_visitor_id';

/* ─── helpers ────────────────────────────────────────────── */

function getStreamId() {
  const parts = window.location.pathname.split('/');
  const idx = parts.indexOf('watch');
  return idx !== -1 && parts[idx + 1] ? parts[idx + 1] : null;
}

function getOrCreateVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_ID_KEY);
    if (!vid) {
      vid = `preview_${Math.random().toString(36).substr(2, 8)}`;
      localStorage.setItem(VISITOR_ID_KEY, vid);
    }
    return vid;
  } catch { return `preview_${Date.now()}`; }
}

function hasUsedPreview(streamId) {
  try {
    const data = JSON.parse(localStorage.getItem(PREVIEW_STORAGE_KEY) || '{}');
    return !!data[streamId];
  } catch { return false; }
}

function markPreviewUsed(streamId) {
  try {
    const data = JSON.parse(localStorage.getItem(PREVIEW_STORAGE_KEY) || '{}');
    data[streamId] = Date.now();
    localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(data));
  } catch { /* best-effort */ }
}

function trackEvent(streamId, eventType, metadata = {}) {
  const visitorId = getOrCreateVisitorId();
  axios.post(`${API_BASE_URL}/api/streams/preview-analytics`, {
    stream_id: streamId,
    event_type: eventType,
    metadata: { ...metadata, visitor_id: visitorId },
  }).catch(() => {});
}

/* ─── component ──────────────────────────────────────────── */

const StreamPreviewPage = () => {
  const streamId = getStreamId();

  const [phase, setPhase] = useState('loading'); // loading | checking_auth | preview | ended | redirecting
  const [streamData, setStreamData] = useState(null);
  const [provider, setProvider] = useState(null); // 'livekit' | 'agora'
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [livekitRoom, setLivekitRoom] = useState(null);
  const [agoraToken, setAgoraToken] = useState(null);
  const [agoraAppId, setAgoraAppId] = useState(null);
  const [agoraChannel, setAgoraChannel] = useState(null);
  const [linkedPartners, setLinkedPartners] = useState([]);
  const [linkedPreview, setLinkedPreview] = useState(false); // Link v2: linked LiveKit preview
  const [countdown, setCountdown] = useState(PREVIEW_SECONDS);
  const timerRef = useRef(null);
  const redirectTimerRef = useRef(null);

  /* ── 1. Auth check → redirect if logged in ── */
  useEffect(() => {
    if (!streamId) {
      window.location.href = '/';
      return;
    }

    trackEvent(streamId, 'shared_link_clicked');

    const checkAuth = async () => {
      // Auth resilience: send the Bearer token (not just the cookie) so a
      // logged-in user with an expired cookie is still recognized, and only
      // treat a 401 (or a retry that also fails) as "logged out".
      const authToken = (() => { try { return localStorage.getItem('authToken'); } catch { return null; } })();
      const meConfig = {
        withCredentials: true,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      };
      const tryMe = async () => axios.get(`${API_BASE_URL}/api/auth/me`, meConfig);
      let res = null;
      try {
        res = await tryMe();
      } catch (err) {
        if (err.response?.status === 401) {
          res = null; // genuinely logged out
        } else {
          // 5xx / network / timeout — retry once before falling through.
          await new Promise((r) => setTimeout(r, 1500));
          try { res = await tryMe(); } catch { res = null; }
        }
      }
      if (res && res.data && res.data.id) {
        // Logged-in user → go straight to full stream (no preview, no landing)
        trackEvent(streamId, 'authenticated_redirect');
        window.location.href = `/app/watch/${streamId}`;
        return;
      }

      // Not logged in — check if preview already used for this stream
      if (hasUsedPreview(streamId)) {
        trackEvent(streamId, 'preview_already_used');
        // Still allow them to see the ended/landing state
        await fetchStreamAndSetup(true);
        return;
      }

      await fetchStreamAndSetup(false);
    };

    checkAuth();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [streamId]);

  /* ── 2. Fetch stream + preview token ── */
  const fetchStreamAndSetup = async (skipPreview) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/streams/${streamId}/preview-token`);
      const data = res.data;

      if (!data.is_active || data.stream_ended) {
        setStreamData({ user_name: data.user_name || data.stream?.user_name, title: data.title || data.stream?.title });
        setPhase('ended');
        trackEvent(streamId, 'stream_ended_on_arrival');
        redirectTimerRef.current = setTimeout(() => { window.location.href = '/'; }, 4000);
        return;
      }

      setStreamData(data.stream);
      setProvider(data.provider || 'livekit');
      setLinkedPartners(Array.isArray(data.linked_partners) ? data.linked_partners : []);
      setLinkedPreview(!!data.linked);

      if (skipPreview) {
        // Already used preview — show a prompt to sign up
        setPhase('redirecting');
        saveStreamContext(streamId);
        redirectTimerRef.current = setTimeout(() => { window.location.href = '/'; }, 3000);
        return;
      }

      // Start live preview — pick the right transport
      if (data.provider === 'agora' && data.token && data.appId && data.channelName) {
        setAgoraToken(data.token);
        setAgoraAppId(data.appId);
        setAgoraChannel(data.channelName);
      } else if (data.token && data.url) {
        setLivekitToken(data.token);
        setLivekitUrl(data.url);
        setLivekitRoom(data.roomName);
      }

      markPreviewUsed(streamId);
      saveStreamContext(streamId);
      trackEvent(streamId, 'preview_started');
      setPhase('preview');
    } catch (err) {
      console.error('Preview fetch error:', err);
      // Stream not found or error — redirect to landing
      setPhase('ended');
      redirectTimerRef.current = setTimeout(() => { window.location.href = '/'; }, 3000);
    }
  };

  /* ── 3. Countdown timer ── */
  useEffect(() => {
    if (phase !== 'preview') return;

    setCountdown(PREVIEW_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          trackEvent(streamId, 'preview_completed');
          trackEvent(streamId, 'redirect_to_landing');
          // Hard redirect after 15 seconds
          window.location.href = '/';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase, streamId]);

  /* ── Save stream context for post-login redirect ── */
  function saveStreamContext(sid) {
    try {
      localStorage.setItem('gems_return_stream', JSON.stringify({
        streamId: sid,
        timestamp: Date.now(),
      }));
    } catch { /* best-effort */ }
  }

  /* ─── RENDER ──────────────────────────────────────────── */

  // Loading / checking auth
  if (phase === 'loading' || phase === 'checking_auth') {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-600 flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-white text-lg font-medium">Joining stream...</p>
        </div>
      </div>
    );
  }

  // Stream has ended
  if (phase === 'ended') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800 flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">This Stream Has Ended</h1>
          <p className="text-gray-400 mb-6">
            {streamData?.user_name ? `${streamData.user_name}'s stream is no longer live.` : 'This stream is no longer live.'}
          </p>
          <p className="text-gray-500 text-sm">Redirecting you to GEMS Live...</p>
          <div className="mt-6">
            <button
              onClick={() => { window.location.href = '/'; }}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl transition-colors"
              data-testid="go-to-landing-btn"
            >
              Go to GEMS Live
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Preview already used → redirect prompt
  if (phase === 'redirecting') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-600/20 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Sign Up to Keep Watching</h1>
          <p className="text-gray-400 mb-6">
            {streamData?.user_name ? `Join GEMS Live to watch ${streamData.user_name}'s stream.` : 'Join GEMS Live to watch this stream.'}
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl transition-colors"
            data-testid="signup-redirect-btn"
          >
            Join GEMS Live
          </button>
        </div>
      </div>
    );
  }

  // ── PREVIEW MODE ──
  return (
    <div className="fixed inset-0 bg-black" data-testid="stream-preview-page">
      <Toaster position="top-right" />

      {/* Video Stream — LiveKit for linked (Link v2) + solo, Agora for legacy linked */}
      <div className="absolute inset-0">
        {provider === 'livekit' && linkedPreview && livekitToken && livekitUrl && livekitRoom ? (
          <LiveKitMultiViewer
            token={livekitToken}
            serverUrl={livekitUrl}
            roomName={livekitRoom}
            hostInfo={streamData ? {
              user_id: streamData.user_id,
              name: streamData.user_name,
              picture: streamData.user_picture,
            } : null}
            linkedPartners={linkedPartners}
            onError={(e) => console.error('[LinkV2 preview] error:', e)}
          />
        ) : provider === 'agora' && agoraToken && agoraAppId && agoraChannel ? (
          <AgoraMultiplayerViewer
            token={agoraToken}
            appId={agoraAppId}
            channelName={agoraChannel}
            streamerName={streamData?.user_name || 'Streamer'}
            hostInfo={streamData ? {
              user_id: streamData.user_id,
              name: streamData.user_name,
              handle: streamData.user_handle,
              picture: streamData.user_picture,
            } : null}
            linkedPartners={linkedPartners}
          />
        ) : livekitToken && livekitUrl ? (
          <LiveKitStreamViewer
            token={livekitToken}
            serverUrl={livekitUrl}
            roomName={livekitRoom}
            streamerName={streamData?.user_name || 'Streamer'}
          />
        ) : (
          /* Fallback: show stream info without actual video */
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
            <div className="text-center">
              {streamData?.user_picture ? (
                <img src={streamData.user_picture} alt="" className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-emerald-500" />
              ) : (
                <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-emerald-600 flex items-center justify-center text-white text-3xl font-bold">
                  {(streamData?.user_name || '?')[0]}
                </div>
              )}
              <h2 className="text-2xl font-bold text-white">{streamData?.user_name || 'Live Stream'}</h2>
              <p className="text-gray-400 mt-2">{streamData?.title || 'Streaming now'}</p>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                  {streamData?.viewer_count || 0} watching
                </span>
                <span className="uppercase text-emerald-400 font-bold">{streamData?.stream_type || 'Live'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay — countdown & streamer info */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
          {/* LIVE badge */}
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 bg-red-600 text-white text-sm font-bold rounded-full flex items-center gap-1.5 animate-pulse">
              <div className="w-2 h-2 bg-white rounded-full"></div>
              LIVE
            </div>
            <div className="flex items-center gap-2">
              {streamData?.user_picture ? (
                <img src={streamData.user_picture} alt="" className="w-8 h-8 rounded-full border-2 border-emerald-500" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                  {(streamData?.user_name || '?')[0]}
                </div>
              )}
              <span className="text-white font-semibold text-sm">{streamData?.user_name}</span>
            </div>
          </div>

          {/* Viewer count */}
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
            {streamData?.viewer_count || 0}
          </div>
        </div>

        {/* Bottom — countdown timer + CTA */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
          {/* Progress bar */}
          <div className="w-full h-1 bg-white/20">
            <div
              className="h-full bg-emerald-500 transition-all duration-1000 ease-linear"
              style={{ width: `${(countdown / PREVIEW_SECONDS) * 100}%` }}
              data-testid="preview-progress-bar"
            />
          </div>

          <div className="p-4 bg-gradient-to-t from-black/90 via-black/70 to-transparent">
            <div className="max-w-lg mx-auto text-center">
              <p className="text-white/90 text-sm mb-3">
                Preview ends in <span className="font-bold text-emerald-400 text-lg" data-testid="preview-countdown">{countdown}s</span>
              </p>
              <button
                onClick={() => {
                  trackEvent(streamId, 'enter_app_click');
                  window.location.href = '/';
                }}
                className="w-full max-w-xs px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-emerald-600/30"
                data-testid="join-gems-btn"
              >
                Join GEMS Live — Keep Watching
              </button>
              <p className="text-gray-500 text-xs mt-2">Sign up free to access all streams</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamPreviewPage;
